import React, { useMemo, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageStorageService from '../../services/imageStorageService';

const RichTextEditor = ({ value, onChange, placeholder, disabled, id }) => {
  const quillRef = useRef(null);

  // Apply alignment to selected image
  useEffect(() => {
    const quill = quillRef?.current?.getEditor();
    if (!quill) return;

    const toolbar = quill?.getModule('toolbar');
    if (!toolbar) return;

    // Override align handler to work with images
    const originalAlignHandler = toolbar?.handlers?.align;
    toolbar.handlers.align = function(value) {
      const range = quill?.getSelection();
      if (!range) return;

      // Check if cursor is on an image
      const [leaf] = quill?.getLeaf(range?.index);
      if (leaf && leaf?.domNode && leaf?.domNode?.tagName === 'IMG') {
        const img = leaf?.domNode;
        
        // Remove existing alignment
        img?.removeAttribute('data-align');
        img.style.display = 'block';
        
        if (value === 'center') {
          img?.setAttribute('data-align', 'center');
          img.style.marginLeft = 'auto';
          img.style.marginRight = 'auto';
        } else if (value === 'right') {
          img?.setAttribute('data-align', 'right');
          img.style.marginLeft = 'auto';
          img.style.marginRight = '0';
        } else {
          img?.setAttribute('data-align', 'left');
          img.style.marginLeft = '0';
          img.style.marginRight = 'auto';
        }
      } else {
        // Apply normal text alignment
        if (originalAlignHandler) {
          originalAlignHandler?.call(this, value);
        }
      }
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['blockquote'],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['clean'],
        ['code-block']
      ],
      handlers: {
        image: function() {
          console.log('Image button clicked');
          
          const input = document.createElement('input');
          input?.setAttribute('type', 'file');
          input?.setAttribute('accept', 'image/png,image/jpeg,image/jpg,image/webp');
          
          input.onchange = async () => {
            const file = input?.files?.[0];
            if (!file) {
              console.log('No file selected');
              return;
            }

            console.log('Image selected:', file?.name);

            try {
              const quill = quillRef?.current?.getEditor();
              if (!quill) {
                console.error('Quill editor not found');
                return;
              }

              const range = quill?.getSelection(true);
              const cursorPosition = range ? range?.index : 0;

              console.log('Cursor position:', cursorPosition);

              quill?.insertText(cursorPosition, 'Uploading image...', 'user');

              const result = await imageStorageService?.uploadImage(file, 'newsletters');
              
              console.log('Uploaded URL:', result?.url);

              quill?.deleteText(cursorPosition, 'Uploading image...'?.length);

              quill?.insertEmbed(cursorPosition, 'image', result?.url, 'user');
              
              // Apply default styling to the inserted image
              setTimeout(() => {
                const editor = quill?.root;
                const images = editor?.querySelectorAll('img');
                images?.forEach(img => {
                  if (!img?.hasAttribute('data-align')) {
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    img.style.borderRadius = '0.5rem';
                    img.style.marginTop = '1rem';
                    img.style.marginBottom = '1rem';
                    img.style.display = 'block';
                    img?.setAttribute('data-align', 'left');
                  }
                });
              }, 100);
              
              quill?.setSelection(cursorPosition + 1);
              
              console.log('Inserted into editor');
            } catch (error) {
              console.error('Image upload failed:', error);
              alert(error?.message || 'Failed to upload image');
            }
          };
          
          input?.click();
        }
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align',
    'link', 'image',
    'blockquote',
    'indent',
    'code-block'
  ];

  return (
    <div className="rich-text-editor-wrapper">
      <ReactQuill
        ref={quillRef}
        id={id}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className="bg-background text-foreground"
      />
    </div>
  );
};

export default RichTextEditor;